import type { BudgetValue } from './BudgetStep';
import type { PrimaryGoalValue } from './PrimaryGoalStep';
import type { Product } from './Product';
import type { Data } from './config';

type BakedRecommendations = {
    [key in BudgetValue]: {
        [key in PrimaryGoalValue]: string[];
    };
};

export function recommend(data: Data, locale: string, products: Product[]): Product[] {
    if (data.deliverableType === 'complete-website') {
        return recommendWebBuilders(data, locale, products).slice(0, 4);
    }

    const customDesignRecommendation = recommendCustomWebDesign(data, products);
    if (customDesignRecommendation) {
        return [customDesignRecommendation];
    }

    return [];
}

function recommendCustomWebDesign(data: Data, products: Product[]): Product | undefined {
    const keyPrefix = data.workingMethod;
    if (data.primaryGoal === 'promote-product-or-service') {
        return getProduct(`${keyPrefix}:landing-page-design`, products);
    }
    return getProduct(`${keyPrefix}:web-design`, products);
}

function getProduct(key: string, products: Product[]): Product | undefined {
    return products.find((p) => p.key === key);
}

function recommendWebBuilders(data: Data, locale: string, products: Product[]): Product[] {
    const bakedWebBuilderRecommendations: BakedRecommendations = {
        low: {
            'promote-business': ['square', 'shopify', 'wix', 'weebly', 'jimdo'],
            'sell-things-online': ['square', 'shopify', 'wix', 'weebly', 'jimdo'],
            'share-ideas': ['wix', 'weebly', 'jimdo'],
            'showcase-work': ['wix', 'weebly', 'jimdo'],
            'promote-product-or-service': ['weebly', 'wix', 'shopify', 'square', 'jimdo'],
            'get-online': ['weebly', 'wix', 'square', 'shopify', 'jimdo'],
        },
        moderate: {
            'promote-business': [
                'squarespace-web-essential',
                'square',
                'shopify',
                'squarespace',
                'wix',
                'weebly',
            ],
            'sell-things-online': [
                'squarespace-web-commerce',
                'shopify',
                'square',
                'squarespace',
                'wix',
                'weebly',
            ],
            'share-ideas': ['squarespace-web-essential', 'wix', 'weebly', 'squarespace', 'jimdo'],
            'showcase-work': ['squarespace-web-essential', 'wix', 'weebly', 'squarespace', 'jimdo'],
            'promote-product-or-service': [
                'squarespace-web-essential',
                'wix',
                'weebly',
                'squarespace',
                'shopify',
                'square',
            ],
            'get-online': ['squarespace-web-essential', 'wix', 'weebly', 'squarespace', 'shopify'],
        },
        high: {
            'promote-business': ['squarespace', 'square', 'shopify', 'wix', 'weebly'],
            'sell-things-online': ['shopify', 'square', 'squarespace', 'wix', 'weebly'],
            'share-ideas': ['wix', 'squarespace', 'weebly', 'jimdo'],
            'showcase-work': ['squarespace', 'wix', 'weebly', 'jimdo'],
            'promote-product-or-service': ['wix', 'squarespace', 'shopify', 'weebly', 'square'],
            'get-online': ['wix', 'weebly', 'squarespace', 'shopify', 'square'],
        },
    };
    const bakedRecommendations =
        bakedWebBuilderRecommendations[data.budget || 'low'][data.primaryGoal || 'get-online'];
    const bakedRecommendationsWithKeyPrefixes = bakedRecommendations.map((r) => `project:${r}`);

    return bakedRecommendationsWithKeyPrefixes.reduce<Product[]>((recommendations, productKey) => {
        const product = getProduct(productKey, products);
        if (!product || !product.isAvailableForLocale(locale)) {
            return recommendations;
        }

        recommendations.push(product);
        return recommendations;
    }, []);
}
